import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Truck, User, ArrowRight, CheckCircle2, Mail, Lock, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { key: "partner", label: "Parceiro", icon: Building2, sub: "Hotel, Agência de Viagens ou Frota que envia reservas" },
  { key: "fleet", label: "Frota", icon: Truck, sub: "Empresa de transporte com veículos e motoristas" },
  { key: "driver", label: "Motorista", icon: User, sub: "Condutor profissional (associa-se depois a uma frota)" },
];

export default function Signup() {
  const [sp] = useSearchParams();
  const initialRole = sp.get("as") || "partner";
  const [role, setRole] = useState(initialRole);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [form, setForm] = useState({
    email: "", password: "", phone: "", address: "",
    // partner
    company_name: "", nif: "", type: "hotel", contact_name: "", postal_code: "",
    // fleet
    legal_representative: "", iban: "",
    // driver
    name: "", languages: ["PT"], base_location: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("A senha deve ter pelo menos 8 caracteres"); return; }
    setLoading(true);
    try {
      const payloadByRole = {
        partner: {
          email: form.email, password: form.password,
          company_name: form.company_name, nif: form.nif, type: form.type,
          contact_name: form.contact_name, phone: form.phone, address: form.address,
          postal_code: form.postal_code,
        },
        fleet: {
          email: form.email, password: form.password,
          company_name: form.company_name, nif: form.nif,
          legal_representative: form.legal_representative,
          phone: form.phone, address: form.address, iban: form.iban,
        },
        driver: {
          email: form.email, password: form.password,
          name: form.name, phone: form.phone,
          languages: form.languages, base_location: form.base_location,
        },
      };
      await axios.post(`${API}/api/signup/${role}`, payloadByRole[role]);
      setSentEmail(form.email);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#f59e0b]" />
          <h1 className="font-display text-3xl mt-4">Inscrição recebida</h1>
          <p className="text-slate-300 mt-3">
            Enviámos um email de confirmação para <span className="text-[#f59e0b] font-mono">{sentEmail}</span>.
            Por favor confirme para prosseguir.
          </p>
          <div className="mt-6 border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100 text-left">
            <p><strong>Próximos passos:</strong></p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Clique no link recebido para confirmar o email</li>
              <li>Aguarde a aprovação da equipa DiTours (24-48h)</li>
              <li>Receberá um email quando a conta for activada</li>
            </ol>
          </div>
          <Link to="/login" className="inline-block mt-6 text-[#f59e0b] hover:underline">← Voltar ao login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white flex items-stretch">
      <aside className="hidden lg:flex flex-col justify-between bg-[#0a0f1e] w-1/2 p-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1e] via-[#1a2140] to-[#0a0f1e]" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#f59e0b]/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#f59e0b]/5 blur-3xl" />
        <div className="relative">
          <div className="font-display text-3xl">DiTours</div>
          <div className="text-[10px] tracking-widest text-[#f59e0b] uppercase font-bold mt-1">Backoffice de Adesão</div>
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl leading-tight">Trabalhe com o mercado premium de Lisboa</h2>
          <p className="text-slate-300 mt-3 max-w-md">
            Junte-se à rede DiTours e receba reservas de hotéis 5*, agências internacionais e clientes VIP em Lisboa e arredores.
          </p>
        </div>
      </aside>

      <main className="flex-1 flex items-center justify-center p-6 lg:p-14 bg-white text-[#0a0f1e]">
        <form onSubmit={submit} className="w-full max-w-lg" data-testid="signup-form">
          <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">Iter 25 · Adesão pública</div>
          <h1 className="font-display text-3xl mt-1">Criar conta</h1>
          <p className="text-sm text-slate-600 mt-2">Escolha o tipo de conta e preencha os dados. Receberá um email para confirmar.</p>

          {/* Role selector */}
          <div className="grid grid-cols-3 gap-2 mt-6">
            {ROLES.map((r) => {
              const active = role === r.key;
              return (
                <button
                  key={r.key} type="button"
                  onClick={() => setRole(r.key)}
                  data-testid={`role-${r.key}`}
                  className={`text-left p-3 border transition-colors ${active ? "border-[#f59e0b] bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <r.icon className={`w-4 h-4 mb-1 ${active ? "text-[#f59e0b]" : "text-slate-500"}`} />
                  <div className="font-semibold text-xs">{r.label}</div>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">{ROLES.find((r) => r.key === role)?.sub}</p>

          {/* Fields */}
          <div className="mt-6 space-y-4">
            <FormField label="Email" testId="signup-email" icon={Mail}>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dt !w-full" placeholder="voce@empresa.pt" />
            </FormField>
            <FormField label="Senha (mín 8 caracteres)" testId="signup-password" icon={Lock}>
              <div className="relative">
                <input required type={showPassword ? "text" : "password"} minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dt !w-full pr-10" placeholder="••••••••" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="signup-password-toggle"
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-400 hover:text-[#f59e0b]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </FormField>

            {role === "partner" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Tipo" testId="signup-type">
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-dt !w-full">
                      <option value="hotel">Hotel</option>
                      <option value="travel_agency">Agência de Viagens</option>
                      <option value="fleet">Frota</option>
                    </select>
                  </FormField>
                  <FormField label="NIF">
                    <input required value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="input-dt !w-full" data-testid="signup-nif" />
                  </FormField>
                </div>
                <FormField label="Nome da empresa">
                  <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input-dt !w-full" data-testid="signup-company" />
                </FormField>
                <FormField label="Pessoa responsável">
                  <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="input-dt !w-full" data-testid="signup-contact" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Telefone">
                    <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dt !w-full" data-testid="signup-phone" placeholder="+351 ..." />
                  </FormField>
                  <FormField label="Código postal">
                    <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="input-dt !w-full" placeholder="1000-000" />
                  </FormField>
                </div>
                <FormField label="Morada">
                  <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-dt !w-full" />
                </FormField>
              </>
            )}

            {role === "fleet" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Nome da empresa">
                    <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input-dt !w-full" data-testid="signup-company" />
                  </FormField>
                  <FormField label="NIF">
                    <input required value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="input-dt !w-full" data-testid="signup-nif" />
                  </FormField>
                </div>
                <FormField label="Representante legal">
                  <input required value={form.legal_representative} onChange={(e) => setForm({ ...form, legal_representative: e.target.value })} className="input-dt !w-full" data-testid="signup-legal-rep" />
                </FormField>
                <FormField label="Telefone">
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dt !w-full" data-testid="signup-phone" />
                </FormField>
                <FormField label="Morada">
                  <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-dt !w-full" />
                </FormField>
                <FormField label="IBAN">
                  <input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input-dt !w-full" placeholder="PT50..." />
                </FormField>
              </>
            )}

            {role === "driver" && (
              <>
                <FormField label="Nome completo">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dt !w-full" data-testid="signup-name" />
                </FormField>
                <FormField label="Telefone / WhatsApp">
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dt !w-full" data-testid="signup-phone" placeholder="+351 ..." />
                </FormField>
                <FormField label="Zona base">
                  <input value={form.base_location} onChange={(e) => setForm({ ...form, base_location: e.target.value })} className="input-dt !w-full" placeholder="Lisboa · Cascais · ..." />
                </FormField>
              </>
            )}
          </div>

          <button
            type="submit" disabled={loading}
            data-testid="signup-submit"
            className="mt-6 w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-bold uppercase tracking-widest text-sm hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? "A enviar…" : (<>Criar conta <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="mt-6 text-center text-xs text-slate-500">
            Já tem conta? <Link to="/login" className="text-[#f59e0b] font-bold hover:underline">Entrar</Link>
          </div>
        </form>
      </main>
    </div>
  );
}

function FormField({ label, icon: Icon, testId, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-1.5 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      <div data-testid={testId}>{children}</div>
    </div>
  );
}
